/**
=========================================================
* Trust Cargo - record a supplier payment
=========================================================

* A form that unfolds as it is filled in:
*
*   supplier  ->  which of that supplier's orders
*             ->  the order's details, so you can see what you are paying
*             ->  payment or deposit, how much, when, and where from
*
* Its own component rather than an AddDialog entity because each step
* depends on the one before it, and the generic dialog only knows how to
* render a flat list of fields.
*
* A supplier IS a shop code, so "that supplier's orders" are the orders
* whose shop_code matches - which is why the order list is fetched per
* supplier rather than filtered client-side.
*/

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

const API = "https://trust-cargo.co/anas_backend/api/v2";

const FIELD_SX = {
  "& .MuiInputBase-root": { minHeight: 52, fontSize: 15 },
  "& .MuiInputBase-input": { padding: "14px 14px" },
  "& .MuiFormHelperText-root": { marginTop: 2, fontSize: 12 },
};

const money = (v) =>
  v === null || v === undefined || v === ""
    ? "-"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().slice(0, 10);

/** One line of the picked order's details. */
function Detail({ label, value, strong, tone }) {
  return (
    <SuiBox>
      <SuiTypography sx={{ fontSize: 11.5, opacity: 0.65 }}>{label}</SuiTypography>
      <SuiTypography
        sx={({ palette }) => ({
          fontSize: strong ? 17 : 15,
          fontWeight: strong ? 800 : 600,
          color: tone ? palette[tone].main : "inherit",
        })}
      >
        {value ?? "-"}
      </SuiTypography>
    </SuiBox>
  );
}

Detail.defaultProps = { strong: false, tone: null, value: "-" };
Detail.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  strong: PropTypes.bool,
  tone: PropTypes.string,
};

function SupplierPaymentDialog({ open, onClose, onSaved, supplierId }) {
  const [suppliers, setSuppliers] = useState([]);
  const [sources, setSources] = useState([]);

  const [supplier, setSupplier] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [order, setOrder] = useState(null);

  const [type, setType] = useState("payment");
  const [amount, setAmount] = useState("");
  const [paidAt, setPaidAt] = useState(today);
  const [source, setSource] = useState(null);
  const [reference, setReference] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  /* ---------------------------------------------------------------- */
  /* opening                                                           */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    if (!open) return;

    setSupplier(null);
    setOrders([]);
    setOrder(null);
    setType("payment");
    setAmount("");
    setPaidAt(today());
    setSource(null);
    setReference("");
    setError("");
    setErrors({});

    const get = (u) =>
      fetch(u, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : []))
        .catch(() => []);

    Promise.all([get(`${API}/suppliers`), get(`${API}/payment-sources`)]).then(([s, src]) => {
      const list = Array.isArray(s) ? s : [];
      setSuppliers(list);
      setSources(Array.isArray(src) ? src : []);

      // opened from a supplier's row - start already on that supplier
      if (supplierId) {
        const found = list.find((x) => String(x.id) === String(supplierId));
        if (found) setSupplier(found);
      }
    });
  }, [open, supplierId]);

  /* the orders belonging to whichever supplier is chosen */
  useEffect(() => {
    setOrder(null);
    setOrders([]);

    if (!supplier) return;

    setLoadingOrders(true);
    fetch(`${API}/suppliers/${supplier.id}/orders`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setOrders(Array.isArray(d?.orders) ? d.orders : []))
      .catch(() => setOrders([]))
      .finally(() => setLoadingOrders(false));
  }, [supplier]);

  /* ---------------------------------------------------------------- */
  /* adding a source without leaving the form                          */
  /* ---------------------------------------------------------------- */

  const addSource = async (name) => {
    const clean = String(name || "").trim();
    if (!clean) return;

    try {
      const res = await axios.post(
        `${API}/payment-sources`,
        { name: clean },
        { headers: { Accept: "application/json", "Content-Type": "application/json" } }
      );

      const created = res?.data?.source;
      if (!created) return;

      setSources((prev) =>
        prev.some((s) => String(s.id) === String(created.id)) ? prev : [...prev, created]
      );
      setSource(created);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not add the source");
    }
  };

  /* ---------------------------------------------------------------- */
  /* saving                                                            */
  /* ---------------------------------------------------------------- */

  const submit = async () => {
    const found = {};
    if (!supplier) found.supplier = "Required";
    if (!String(amount).trim() || Number(amount) <= 0) found.amount = "Required";
    if (!paidAt) found.paidAt = "Required";

    setErrors(found);
    if (Object.keys(found).length) return;

    setSaving(true);
    setError("");

    try {
      await axios.post(
        `${API}/supplier-payments`,
        {
          supplier_id: supplier.id,
          order_id: order ? order.id : null,
          type,
          payment_source_id: source ? source.id : null,
          paid_at: paidAt,
          amount: Number(amount),
          reference: reference || null,
          // the amount is already in the accounting currency
          rate: 1,
          currency: "CNY",
        },
        { headers: { Accept: "application/json", "Content-Type": "application/json" } }
      );

      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(e?.response?.data?.message || "Could not save the payment");
    }
  };

  const TYPES = [
    { key: "payment", label: "Payment", icon: "payments" },
    { key: "deposit", label: "Deposit", icon: "savings" },
  ];

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: ({ borders, boxShadows }) => ({
          borderRadius: borders.borderRadius.lg,
          boxShadow: boxShadows.xl,
        }),
      }}
    >
      <DialogTitle
        sx={({ palette, functions: { rgba } }) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${rgba(palette.dark.main, 0.08)}`,
          pb: 1.5,
        })}
      >
        <SuiTypography variant="h5" fontWeight="bold">
          Record Supplier Payment
        </SuiTypography>
        <Icon
          onClick={saving ? undefined : onClose}
          sx={({ palette, functions: { rgba } }) => ({
            cursor: saving ? "default" : "pointer",
            fontSize: 26,
            color: rgba(palette.dark.main, 0.45),
            "&:hover": { color: palette.error.main },
          })}
        >
          close
        </Icon>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <SuiBox mb={2}>
            <Alert severity="error">{error}</Alert>
          </SuiBox>
        )}

        <SuiBox
          sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.5 }}
        >
          {/* 1. supplier */}
          <Autocomplete
            options={suppliers}
            value={supplier}
            onChange={(e, v) => setSupplier(v)}
            getOptionLabel={(o) =>
              o?.code && o.code !== o.name ? `${o.code} - ${o.name}` : o?.name || ""
            }
            isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
            size="medium"
            fullWidth
            openOnFocus
            autoHighlight
            sx={FIELD_SX}
            renderInput={(p) => (
              <TextField
                {...p}
                label="Supplier"
                error={Boolean(errors.supplier)}
                helperText={errors.supplier || " "}
                InputLabelProps={{ shrink: true }}
                placeholder="Type to search..."
              />
            )}
          />

          {/* 2. which of that supplier's orders - only once one is chosen */}
          <Autocomplete
            options={orders}
            value={order}
            onChange={(e, v) => setOrder(v)}
            disabled={!supplier || loadingOrders}
            getOptionLabel={(o) => o?.label || o?.shop_code || ""}
            isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
            size="medium"
            fullWidth
            openOnFocus
            autoHighlight
            sx={FIELD_SX}
            noOptionsText="This supplier has no orders"
            // room for two-line rows without it turning into a scroll bar
            ListboxProps={{ style: { maxHeight: 340 } }}
            /**
             * Each option carries the figures that decide which order this
             * is, so the choice is made in the list rather than by picking
             * one and checking afterwards.
             */
            renderOption={(props, o) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li {...props} key={o.id}>
                <SuiBox
                  sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1.5, py: 0.6 }}
                >
                  <SuiBox sx={{ flex: 1, minWidth: 0 }}>
                    <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* the row's own id, the number to quote when looking
                          it up directly */}
                      <SuiTypography
                        sx={({ palette, functions: { rgba } }) => ({
                          fontSize: 12.5,
                          fontWeight: 700,
                          fontVariantNumeric: "tabular-nums",
                          color: rgba(palette.dark.main, 0.45),
                        })}
                      >
                        {`#${o.id}`}
                      </SuiTypography>

                      <SuiTypography sx={{ fontSize: 15, fontWeight: 800 }}>
                        {o.shop_code}
                      </SuiTypography>
                      {o.status && (
                        <Chip
                          label={o.status}
                          size="small"
                          sx={({ palette, functions: { rgba } }) => ({
                            height: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            backgroundColor: rgba(palette.primary.main, 0.16),
                            color: palette.dark.main,
                          })}
                        />
                      )}
                    </SuiBox>

                    <SuiTypography
                      sx={{
                        fontSize: 12.5,
                        opacity: 0.7,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {[o.customer, o.container, o.goods, o.date].filter(Boolean).join(" · ")}
                    </SuiTypography>
                  </SuiBox>

                  <SuiBox sx={{ textAlign: "end", flexShrink: 0 }}>
                    <SuiTypography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                      {`${money(o.final_amount)} final`}
                    </SuiTypography>
                    <SuiTypography
                      sx={({ palette }) => ({
                        fontSize: 12,
                        fontWeight: 700,
                        color: Number(o.balance) > 0 ? palette.error.main : palette.success.main,
                      })}
                    >
                      {`${money(o.balance)} balance`}
                    </SuiTypography>
                  </SuiBox>
                </SuiBox>
              </li>
            )}
            renderInput={(p) => (
              <TextField
                {...p}
                label="Order"
                InputLabelProps={{ shrink: true }}
                helperText={
                  // eslint-disable-next-line no-nested-ternary
                  !supplier
                    ? "Choose a supplier first"
                    : loadingOrders
                    ? "Loading orders..."
                    : `${orders.length} order(s)`
                }
                placeholder={supplier ? "Type to search..." : ""}
                InputProps={{
                  ...p.InputProps,
                  endAdornment: (
                    <>
                      {loadingOrders && <CircularProgress size={18} />}
                      {p.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* 3. everything about the order that was picked */}
          {order && (
            <SuiBox
              sx={({ palette, borders, functions: { rgba } }) => ({
                gridColumn: "1 / -1",
                p: 2.5,
                borderRadius: borders.borderRadius.lg,
                border: `1px solid ${rgba(palette.dark.main, 0.1)}`,
                backgroundColor: rgba(palette.primary.main, 0.05),
              })}
            >
              <SuiBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                  gap: 2,
                }}
              >
                <Detail label="Order ID" value={`#${order.id}`} />
                <Detail label="Shop code" value={order.shop_code} strong />
                <Detail label="Customer" value={order.customer} />
                <Detail label="Container" value={order.container} />
                <Detail label="Status" value={order.status} />

                <Detail label="Order date" value={order.date} />
                <Detail label="Goods" value={order.goods} />
                <Detail
                  label="CBM / KG"
                  value={`${order.total_cbm || 0} / ${order.total_kg || 0}`}
                />
                <Detail label="First amount" value={money(order.first_amount)} />

                <Detail label="Final amount" value={money(order.final_amount)} strong />
                <Detail label="Deposit" value={money(order.deposit)} />
                <Detail label="Paid" value={money(order.paid)} />
                <Detail
                  label="Balance"
                  value={money(order.balance)}
                  strong
                  tone={Number(order.balance) > 0 ? "error" : "success"}
                />
              </SuiBox>
            </SuiBox>
          )}

          {/* 4. payment or deposit */}
          <SuiBox sx={{ gridColumn: "1 / -1" }}>
            <SuiTypography sx={{ fontSize: 12.5, opacity: 0.7, mb: 1 }}>Payment type</SuiTypography>
            <SuiBox sx={{ display: "flex", gap: 1.5 }}>
              {TYPES.map((t) => (
                <SuiBox
                  key={t.key}
                  component="button"
                  type="button"
                  onClick={() => setType(t.key)}
                  sx={({ palette, borders, functions: { rgba } }) => ({
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontWeight: 700,
                    fontSize: 15,
                    py: 1.5,
                    borderRadius: borders.borderRadius.md,
                    color: type === t.key ? palette.dark.main : rgba(palette.dark.main, 0.55),
                    border: `1px solid ${
                      type === t.key ? palette.primary.main : rgba(palette.dark.main, 0.15)
                    }`,
                    backgroundColor:
                      type === t.key ? rgba(palette.primary.main, 0.14) : "transparent",
                  })}
                >
                  <Icon>{t.icon}</Icon>
                  {t.label}
                </SuiBox>
              ))}
            </SuiBox>
          </SuiBox>

          {/* 5. the money */}
          <TextField
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={Boolean(errors.amount)}
            helperText={errors.amount || " "}
            fullWidth
            size="medium"
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: "any" }}
            sx={FIELD_SX}
          />

          <TextField
            label="Payment date"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            error={Boolean(errors.paidAt)}
            helperText={errors.paidAt || " "}
            fullWidth
            size="medium"
            InputLabelProps={{ shrink: true }}
            sx={FIELD_SX}
          />

          {/**
           * 6. where the money came from. freeSolo so a source that does not
           * exist yet is typed straight in and created on the spot - the
           * list is data, not a fixed set.
           */}
          <Autocomplete
            options={sources}
            value={source}
            freeSolo
            selectOnFocus
            handleHomeEndKeys
            onChange={(e, v) => {
              if (typeof v === "string") addSource(v);
              else if (v?.isNew) addSource(v.name);
              else setSource(v);
            }}
            filterOptions={(opts, params) => {
              const text = params.inputValue.trim();
              const filtered = opts.filter((o) =>
                o.name.toLowerCase().includes(text.toLowerCase())
              );

              const exists = opts.some((o) => o.name.toLowerCase() === text.toLowerCase());
              if (text && !exists) {
                filtered.push({ id: `new-${text}`, name: text, isNew: true });
              }

              return filtered;
            }}
            getOptionLabel={(o) => (typeof o === "string" ? o : o?.name || "")}
            isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
            renderOption={(props, o) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li {...props} key={o.id}>
                {o.isNew ? (
                  <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon sx={{ fontSize: "18px !important" }}>add</Icon>
                    {`Add "${o.name}"`}
                  </SuiBox>
                ) : (
                  o.name
                )}
              </li>
            )}
            size="medium"
            fullWidth
            sx={FIELD_SX}
            renderInput={(p) => (
              <TextField
                {...p}
                label="Payment source"
                InputLabelProps={{ shrink: true }}
                helperText="Type a new one to add it"
                placeholder="Cash box, bank..."
              />
            )}
          />

          <TextField
            label="Reference / transfer no."
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            fullWidth
            size="medium"
            InputLabelProps={{ shrink: true }}
            helperText=" "
            sx={FIELD_SX}
          />
        </SuiBox>
      </DialogContent>

      <DialogActions
        sx={({ palette, functions: { rgba } }) => ({
          px: 3,
          pb: 2.5,
          pt: 1,
          gap: 1.5,
          borderTop: `1px solid ${rgba(palette.dark.main, 0.07)}`,
        })}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          size="large"
          sx={{ minWidth: 120, fontSize: 15 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={submit}
          disabled={saving}
          sx={{ minWidth: 170, fontSize: 15 }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SupplierPaymentDialog.defaultProps = { supplierId: null, onSaved: () => {} };

SupplierPaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  // when opened from a supplier's row, that supplier starts selected
  supplierId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default SupplierPaymentDialog;
